import { NavLink, useNavigate } from "react-router-dom";
import {
  FiHome,
  FiLogOut,
  FiSearch,
  FiFileText,
  FiUser,
  FiUpload,
  FiLock,
  FiUserX,
  FiMessageCircle,
  FiSettings,
  FiLayers,
  FiBriefcase,
  FiSmartphone,
  FiMapPin,
  FiNavigation,
  FiList,
  FiX,
  FiGrid,
  FiCreditCard,
  FiDatabase,
  FiHash,
  FiMail,
  FiCheckCircle,
} from "react-icons/fi";
import useAuth from "../../hooks/useAuth";
import { actsAsAgencyAdmin } from "../../utils/permissions";
import useIsMobile from "../../hooks/useIsMobile";
import { STAFF_PROFILE_MENU_LABEL } from "../../constants/companyLabels";
import { MOBILE_ADMIN_SIDEBAR_PATHS } from "../../constants/mobileControlPanel";
import { useMobileNav } from "../../contexts/MobileNavContext";
import WorkModeToggle from "./WorkModeToggle";
import "../../styles/sidebar.css";

export default function Sidebar() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { closeNav } = useMobileNav();

  const isRepoAdmin = actsAsAgencyAdmin(auth);

  const menu = [
    { to: "/home", label: "Home", icon: <FiHome />, permissionKey: "home.view" },
    { to: "/control-panel", label: "Control Panel", icon: <FiGrid />, mobileAdminOnly: true, permissionKey: "control_panel.view" },
    {
      to: "/profile",
      label: STAFF_PROFILE_MENU_LABEL,
      icon: isRepoAdmin ? <FiBriefcase /> : <FiUser />,
      permissionKey: "profile.view",
    },
    { to: "/id-card", label: "ID Card", icon: <FiCreditCard />, permissionKey: "id_card.view" },
    { to: "/plans", label: "Plans", icon: <FiLayers />, permissionKey: "plans.view" },
    { to: "/cases", label: "Trace Cases", icon: <FiList />, permissionKey: "cases.view" },
    { to: "/seized-vehicles", label: "Seized vehicles", icon: <FiCheckCircle />, permissionKey: "confirmation.view" },
    { to: "/find-vehicles", label: "Find Vehicles", icon: <FiSearch />, permissionKey: "find_vehicles.view" },
    { to: "/field-map", label: "Field Map", icon: <FiMapPin />, fleetMapOnly: true, permissionKey: "field_map.view" },
    { to: "/live-tracking", label: "Live Tracking", icon: <FiNavigation />, permissionKey: "live_tracking.view" },
    { to: "/reports", label: "Reports", icon: <FiFileText />, permissionKey: "reports.view" },
    { to: "/users", label: "All users", icon: <FiUser />, permissionKey: "users.view" },
    { to: "/roles-permissions", label: "Roles & Permissions", icon: <FiSettings />, permissionKey: "roles_permissions.manage" },
    { to: "/email-configuration", label: "Email Configuration", icon: <FiMail />, permissionKey: "email_config.manage" },
    { to: "/bank-details", label: "Agency Upload Repo List Details", icon: <FiUpload />, permissionKey: "upload_records.view" },
    { to: "/bank-records", label: "Bank/NBFC Repo List Details", icon: <FiDatabase />, permissionKey: "bank_records.view" },
    { to: "/code-bank-nbfc", label: "Code Details Bank/NBFC", icon: <FiHash />, permissionKey: "code_bank_nbfc.view" },
    { to: "/otps", label: "OTPs", icon: <FiLock />, permissionKey: "otps.view" },
    { to: "/blacklist", label: "Blacklist", icon: <FiUserX />, permissionKey: "blacklist.view" },
    { to: "/feedback", label: "Feedback", icon: <FiMessageCircle />, permissionKey: "feedback.view" },
  ];

  const permissions = Array.isArray(auth?.user?.permissions)
    ? auth.user.permissions
    : Array.isArray(auth?.permissions)
      ? auth.permissions
      : [];
  const useMobileAdminMenu = isMobile && isRepoAdmin;

  const handleLogout = () => {
    closeNav();
    logout();
    navigate("/", { replace: true });
  };

  const filteredMenu = menu.filter((item) => {
    if (item.mobileAdminOnly && !useMobileAdminMenu) return false;
    if (
      item.fleetMapOnly &&
      !["REPO_ADMIN", "TEAM_LEADER"].includes(auth?.user?.role) &&
      !isRepoAdmin
    ) {
      return false;
    }
    const isAdminAllowed = isRepoAdmin;
    const hasPermission = isAdminAllowed || permissions.includes(item.permissionKey);
    if (!hasPermission) return false;
    if (useMobileAdminMenu) {
      return MOBILE_ADMIN_SIDEBAR_PATHS.includes(item.to);
    }
    return true;
  });

  return (
    <aside className="sidebar sidebar--repo">
      <div className="sidebar-head sidebar-head--repo">
        <button
          type="button"
          className="sidebar-close"
          onClick={closeNav}
          aria-label="Close menu"
        >
          <FiX aria-hidden />
        </button>
      </div>

      <nav className="sidebar-nav">
        {filteredMenu.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={closeNav}
            className={({ isActive }) =>
              `sidebar-item ${isActive ? "active" : ""}`
            }
          >
            <span className="item-icon">{item.icon}</span>
            <span className="item-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        <WorkModeToggle variant="sidebar" />
        <button type="button" className="sidebar-logout" onClick={handleLogout}>
          <span className="item-icon">
            <FiLogOut aria-hidden />
          </span>
          <span className="item-label">Logout</span>
        </button>
      </div>
    </aside>
  );
}